import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import InlineForm from './InlineForm';
import Icon from './Icon';
import { ICONS } from '../lib/icons';
import { toId } from '../lib/ids';

// An in-page form for creating or editing an IAM user group. When `group` is
// provided the form starts in edit mode (prefilled, with its current members)
// and saves via PATCH; otherwise it creates via POST. The OCI tenant is chosen
// from a dropdown populated live from the backend, with a refresh button.
// Members are added and removed one at a time and applied on save. Contracts
// are managed on the Contract management page, not here.
export default function UserGroupForm({ users = [], group, onClose, onSaved }) {
  const editing = !!group;
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [tenancyId, setTenancyId] = useState(group?.tenancyId || '');
  const [discount, setDiscount] = useState(String(group?.discount ?? 0));
  const [tenants, setTenants] = useState([]);
  // Kept as strings so they match the <option value> the dropdown emits;
  // converted to numeric ids when the payload is built.
  const [memberIds, setMemberIds] = useState(() =>
    (group?.members || []).map((m) => String(m.id)),
  );
  // The dropdown's current choice, staged until + is pressed.
  const [toAdd, setToAdd] = useState('');
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadTenants = useCallback(() => {
    setLoadingTenants(true);
    setError(null);
    api
      .getOrganizationTenancies('oci')
      .then((list) => {
        setTenants(list || []);
        setTenancyId((cur) => cur || list?.[0]?.tenancyId || '');
      })
      .catch((e) => setError(`Could not load tenants: ${e.message}`))
      .finally(() => setLoadingTenants(false));
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Keep the group's current tenant selectable even if it isn't in the list.
  const tenantOptions = useMemo(() => {
    const opts = [...tenants];
    if (
      group?.tenancyId &&
      !opts.some((t) => t.tenancyId === group.tenancyId)
    ) {
      opts.unshift({ tenancyId: group.tenancyId, name: group.tenancyName });
    }
    return opts;
  }, [tenants, group]);

  // Members are added one at a time: pick from the dropdown and press +, press
  // − on a row to drop them. Nothing is persisted until Save.
  const userById = useMemo(
    () => Object.fromEntries(users.map((u) => [String(u.id), u])),
    [users]
  );
  const availableUsers = useMemo(
    () => users.filter((u) => !memberIds.includes(String(u.id))),
    [users, memberIds]
  );

  // Keep the dropdown on a valid choice as the available list shrinks.
  useEffect(() => {
    setToAdd((cur) =>
      cur && availableUsers.some((u) => String(u.id) === cur)
        ? cur
        : availableUsers[0]?.id != null
        ? String(availableUsers[0].id)
        : ''
    );
  }, [availableUsers]);

  const addMember = () => {
    if (!toAdd || memberIds.includes(toAdd)) return;
    setMemberIds((ids) => [...ids, toAdd]);
  };

  const removeMember = (id) =>
    setMemberIds((ids) => ids.filter((x) => x !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Group name is required');
      return;
    }
    const discountPct = Number(discount || 0);
    if (!Number.isFinite(discountPct) || discountPct < 0 || discountPct > 100) {
      setError('Discount must be a percentage between 0 and 100');
      return;
    }
    const tenant = tenantOptions.find((t) => t.tenancyId === tenancyId);
    const payload = {
      discount: discountPct,
      name: name.trim(),
      description: description.trim(),
      tenancyId,
      tenancyName: tenant?.name || '',
      userIds: memberIds.map(toId),
    };
    setSubmitting(true);
    setError(null);
    try {
      const saved = editing
        ? await api.updateUserGroup(group.id, payload)
        : await api.createUserGroup(payload);
      onSaved(saved);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <InlineForm
      title={editing ? 'Edit user group' : 'Create user group'}
      onSubmit={submit}
      onCancel={onClose}
      submitting={submitting}
      submitLabel={editing ? 'Save changes' : 'Create group'}
      error={error}
    >
      <label className="field">
        <span>User group name</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Administrators"
          autoFocus
          required
        />
      </label>

      <label className="field">
        <span>Description (optional)</span>
        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this group for?"
        />
      </label>

      <label className="field">
        <span>Billing discount (%)</span>
        <input
          className="input"
          type="number"
          min="0"
          max="100"
          step="0.01"
          value={discount}
          onChange={(e) => setDiscount(e.target.value)}
        />
        <span className="field__hint">
          Taken off everything this group is billed. 0 means no discount.
        </span>
      </label>

      <label className="field">
        <span>OCI tenant</span>
        <div className="field-with-btn">
          <select
            className="input"
            value={tenancyId}
            onChange={(e) => setTenancyId(e.target.value)}
            disabled={loadingTenants || tenantOptions.length === 0}
          >
            {tenantOptions.length === 0 ? (
              <option value="">
                {loadingTenants ? 'Loading tenants…' : 'No tenants available'}
              </option>
            ) : (
              tenantOptions.map((t) => (
                <option key={t.tenancyId} value={t.tenancyId}>
                  {t.name || t.tenancyId}
                  {t.role ? ` · ${t.role}` : ''}
                </option>
              ))
            )}
          </select>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={loadTenants}
            disabled={loadingTenants}
            title="Refresh tenant list from OCI"
          >
            {loadingTenants ? (
              '…'
            ) : (
              <>
                <Icon name={ICONS.refresh} /> Refresh
              </>
            )}
          </button>
        </div>
      </label>

      <div className="field">
        <span>Members</span>

        {/* Current members, each removable. */}
        {memberIds.length > 0 ? (
          <ul className="picklist">
            {memberIds.map((id) => {
              const u = userById[id];
              return (
                <li key={id} className="picklist__item">
                  <span className="picklist__main">{u?.name || `User ${id}`}</span>
                  <span className="picklist__meta">
                    {u?.email || 'no longer available'}
                  </span>
                  <button
                    type="button"
                    className="btn btn--danger-ghost btn--sm picklist__btn"
                    onClick={() => removeMember(id)}
                    aria-label={`Remove ${u?.name || id}`}
                    title="Remove this member"
                  >
                    −
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="field__hint">
            No members yet — pick someone below and press <strong>+</strong>.
          </p>
        )}

        {/* Add a member: only users not already in the group are offered. */}
        <div className="field-with-btn">
          <select
            className="input"
            value={toAdd}
            onChange={(e) => setToAdd(e.target.value)}
            disabled={availableUsers.length === 0}
          >
            {availableUsers.length === 0 ? (
              <option value="">
                {users.length === 0 ? 'No users available' : 'All users added'}
              </option>
            ) : (
              availableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} — {u.email}
                </option>
              ))
            )}
          </select>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={addMember}
            disabled={availableUsers.length === 0}
            title="Add this user to the group"
          >
            + Add
          </button>
        </div>
        <span className="field__hint">
          {memberIds.length} member{memberIds.length === 1 ? '' : 's'}. Changes
          apply when you save.
        </span>
      </div>
    </InlineForm>
  );
}
